import { Shadows, ThemeOptions } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { darkNavy, red, yellow, blue, vividPurple, orange, purple, green, vividGreen, grayNavy } from '../color-pallete';
import { lightTheme } from './light';

export const darkTheme = {
  ...lightTheme,
  palette: {
    ...lightTheme.palette,
    mode: 'dark',
    primary: {
      main: vividPurple[700],
      onMain: vividPurple[50],
      container: vividPurple[400],
      onContainer: vividPurple[800],
      inverse: vividPurple[700],
      onInverse: vividPurple[50],
      dark: vividPurple[800],
      light: vividPurple[300],
      outline: vividPurple[600],
      outlineVariant: vividPurple[500],
      //Required by mui, don't use
      contrastText: vividPurple[50],
    },
    secondary: {
      main: grayNavy[800],
      onMain: grayNavy[50],
      container: grayNavy[500],
      onContainer: grayNavy[900],
      inverse: grayNavy[900],
      onInverse: grayNavy[50],
      dark: grayNavy[600],
      light: grayNavy[400],
      outline: grayNavy[800],
      outlineVariant: grayNavy[600],
      //Required by mui, don't use
      contrastText: grayNavy[50],
    },
    error: {
      main: red[600],
      onMain: red[50],
      container: red[200],
      onContainer: red[800],
      inverse: red[700],
      onInverse: red[50],
      dark: red[700],
      light: red[300],
      outline: red[600],
      outlineVariant: red[300],
      //Required by mui, don't use
      contrastText: red[50],
    },
    warning: {
      main: yellow[500],
      onMain: yellow[50],
      container: yellow[200],
      onContainer: yellow[700],
      inverse: yellow[800],
      onInverse: yellow[50],
      dark: yellow[700],
      light: yellow[300],
      outline: yellow[500],
      outlineVariant: yellow[300],
      //Required by mui, don't use
      contrastText: yellow[50],

    },
    info: {
      main: blue[600],
      onMain: blue[50],
      container: blue[200],
      onContainer: blue[800],
      inverse: blue[800],
      onInverse: blue[50],
      dark: blue[700],
      light: blue[300],
      outline: blue[600],
      outlineVariant: blue[300],
      //Required by mui, don't use
      contrastText: blue[50],
    },
    success: {
      main: green[400],
      onMain: green[50],
      container: green[100],
      onContainer: green[600],
      inverse: green[700],
      onInverse: green[100],
      dark: green[800],
      light: green[300],
      outline: green[400],
      outlineVariant: green[300],
      //Required by mui, don't use
      contrastText: green[50],
    },
    orange: {
      main: orange[500],
      onMain: orange[990],
      container: orange[200],
      onContainer: orange[800],
      inverse: orange[900],
      onInverse: orange[50],
      dark: orange[700],
      light: orange[300],
      outline: orange[500],
      outlineVariant: orange[300],
      //Required by mui, don't use
      contrastText: blue[990],
    },
    purple: {
      main: purple[700],
      onMain: purple[50],
      container: purple[300],
      onContainer: purple[800],
      inverse: purple[700],
      onInverse: purple[50],
      dark: purple[700],
      light: purple[300],
      outline: purple[600],
      outlineVariant: purple[400],
      //Required by mui, don't use
      contrastText: purple[50],
    },
    brand: {
      main: vividGreen[400],
      onMain: vividGreen[300],
      container: vividGreen[200],
      onContainer: vividGreen[800],
      inverse: vividGreen[700],
      onInverse: vividGreen[100],
      dark: vividGreen[600],
      light: vividGreen[300],
      outline: vividGreen[400],
      outlineVariant: vividGreen[200],
      //Required by mui, don't use
      contrastText: vividGreen[300],
    },
    neutral: {
      //container
      surface: grayNavy[200],
      surfaceDim: grayNavy[100],
      surfaceBright: grayNavy[300],
      surfaceVariant: grayNavy[200],
      inverse: grayNavy[920],
      surfaceTint: vividPurple[300],
      containerLowest: grayNavy[70],
      containerLow: grayNavy[100],
      container: grayNavy[200],
      containerHigh: grayNavy[300],
      containerHighest: grayNavy[300],
      background: grayNavy[100],
      //on
      onSurface: grayNavy[900],
      onSurfaceVariant: grayNavy[800],
      onInverse: grayNavy[200],
      onBackground: grayNavy[800],
      disabled: grayNavy[500],
      //outline
      outline: grayNavy[700],
      outlineVariant: grayNavy[500],
      //extras      
      shadow: grayNavy[300],
      scrim: grayNavy[100],
      fixed: grayNavy[400],
      dark: grayNavy[50],
      //Required by mui, don't use
      main: darkNavy[100],
    },
    text: {
      primary: grayNavy[900],
      secondary: grayNavy[800],
    },
    background: {
      ...lightTheme.palette!.background,
      default: darkNavy[100],
      paper: darkNavy[200],
    },
    action: {
      ...lightTheme.palette!.action,
      disabled: alpha(darkNavy[900], 0.38),
      disabledBackground: alpha(darkNavy[900], 0.12),
      disabledBorder: alpha(darkNavy[900], 0.12),
    },
    divider: darkNavy[300],
  },
  shadows: (() => {
    //MUI requires to have 25 shadows, but curently in our design system is 6;
    const shadows = new Array(25).fill('0px 8px 10px 0px rgba(5, 7, 15, 0.20), 0px 24px 32px 4px rgba(5, 7, 15, 0.24)') as Shadows;
    shadows[0] = 'none';
    shadows[1] = '0px 1px 1px 0px rgba(5, 7, 15, 0.20), 0px 1px 4px 0px rgba(0, 0, 0, 0.24)';
    shadows[2] = '0px 2px 4px 0px rgba(5, 7, 15, 0.20), 0px 4px 8px 1px rgba(0, 0, 0, 0.24)';
    shadows[3] = '0px 4px 6px 0px rgba(5, 7, 15, 0.20), 0px 8px 16px 2px rgba(0, 0, 0, 0.24)';
    shadows[4] = '0px 6px 8px 0px rgba(5, 7, 15, 0.20), 0px 16px 24px 3px rgba(0, 0, 0, 0.24)';
    shadows[5] = '0px 8px 10px 0px rgba(5, 7, 15, 0.20), 0px 24px 32px 4px rgba(5, 7, 15, 0.24)';
    return shadows;
  })()
} as ThemeOptions;
